import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import color from "../../../Constants/colors";
import { DocumentCraftingFormField, DocumentCraftingUpload } from "../../../Models/documentCrafting";
import { InfoRow } from "../Common/DocumentCraftingUi";
import { localizedText } from "../Common/useDocumentCraftingEnumLabels";
import {
  FormValues,
  PendingFiles,
  asRecord,
  isEmptyValue,
} from "./documentFormModel";

interface Props {
  fields: DocumentCraftingFormField[];
  values: FormValues;
  files: PendingFiles;
  /** Earlier uploads per file field path, to name the ticked ones. */
  uploads?: Record<string, DocumentCraftingUpload[]>;
}

const formatDateValue = (value: string, language: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ).toLocaleDateString(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const optionText = (field: DocumentCraftingFormField, value: unknown, language: string) => {
  const option = field.options?.find((item) => item.value === value);
  return option
    ? localizedText(option.label, language) || option.value
    : String(value);
};

/** A value as one line of text; "" when nothing was entered. */
const valueText = (
  field: DocumentCraftingFormField,
  value: unknown,
  t: TFunction,
  language: string,
): string => {
  if (field.type === "boolean") {
    return value === true ? t("create.summary.yes") : t("create.summary.no");
  }
  if (isEmptyValue(value)) return "";
  switch (field.type) {
    case "select":
      return optionText(field, value, language);
    case "multiselect":
      return Array.isArray(value)
        ? value.map((item) => optionText(field, item, language)).join(", ")
        : "";
    case "number":
      return typeof value === "number" ? value.toLocaleString(language) : "";
    case "date":
      return typeof value === "string" ? formatDateValue(value, language) : "";
    case "group":
      return childrenText(field.fields ?? [], asRecord(value), t, language);
    case "list": {
      const items = Array.isArray(value) ? value : [];
      if (!field.item_fields?.length) {
        return items.map(String).filter(Boolean).join(", ");
      }
      return items
        .map((item) =>
          childrenText(field.item_fields ?? [], asRecord(item), t, language),
        )
        .filter(Boolean)
        .join("; ");
    }
    default:
      return Array.isArray(value) ? value.map(String).join(", ") : String(value);
  }
};

/** "Label: value · Label: value" of the filled fields of a group or row. */
const childrenText = (
  children: DocumentCraftingFormField[],
  record: Record<string, unknown>,
  t: TFunction,
  language: string,
): string =>
  children
    .map((child) => {
      const text =
        child.type === "boolean" && record[child.key] !== true
          ? ""
          : valueText(child, record[child.key], t, language);
      return text ? `${localizedText(child.label, language)}: ${text}` : "";
    })
    .filter(Boolean)
    .join(" · ");

const fileNames = (
  path: string,
  value: unknown,
  files: PendingFiles,
  uploads?: Record<string, DocumentCraftingUpload[]>,
) => {
  const ids = Array.isArray(value) ? value : [];
  return [
    ...(uploads?.[path] ?? [])
      .filter((upload) => ids.includes(upload.id))
      .map((upload) => upload.filename),
    ...(files[path] ?? []).map((item) => item.file.name),
  ];
};

/** Read-only overview of the entered values (review step). */
const FormSummary: React.FC<Props> = ({ fields, values, files, uploads }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const language = i18n.language;

  if (!fields.length) {
    return (
      <Typography sx={{ fontSize: 14, color: color.gray500 }}>
        {t("create.summary.nothing")}
      </Typography>
    );
  }

  const renderValue = (field: DocumentCraftingFormField): React.ReactNode => {
    const value = values[field.key];
    if (field.type === "file") {
      return fileNames(field.key, value, files, uploads).join(", ");
    }
    if (field.type === "list" && field.item_fields?.length) {
      const items = Array.isArray(value) ? value : [];
      if (!items.length) return "";
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {items.map((item, index) => (
            <Box component="li" key={index} sx={{ mb: 0.25 }}>
              {childrenText(field.item_fields ?? [], asRecord(item), t, language) ||
                t("create.summary.emptyItem")}
            </Box>
          ))}
        </Box>
      );
    }
    const text = valueText(field, value, t, language);
    return text ? (
      <Box
        component="span"
        sx={{ whiteSpace: field.type === "textarea" ? "pre-line" : undefined }}
      >
        {text}
      </Box>
    ) : (
      ""
    );
  };

  return (
    <Box>
      {fields.map((field) => (
        <InfoRow
          key={field.key}
          label={localizedText(field.label, language) || field.key}
          value={renderValue(field)}
        />
      ))}
    </Box>
  );
};

export default FormSummary;
